import { config } from "@/config";
import { activeJwt } from "@/auth/session";
import type { MusicDebugItem } from "@/audio/SpatialMusicLayer";
import type { SheepDebugItem } from "@/world/objects/SheepNPC";

const GLOBAL_POLL_MS = 60_000;

export class StatsPanel {
  private statusEl = document.getElementById("stat-status")!;
  private zoneEl = document.getElementById("stat-zone")!;
  private positionEl = document.getElementById("stat-position")!;
  private onlineEl = document.getElementById("stat-online")!;
  private sessionEl = document.getElementById("stat-session")!;
  private messagesEl = document.getElementById("stat-messages")!;
  private registeredEl = document.getElementById("stat-registered")!;
  private globalOnlineEl = document.getElementById("stat-global-online")!;
  private cacheBackendEl = document.getElementById("stat-cache-backend")!;
  private cacheKeysEl = document.getElementById("stat-cache-keys")!;
  private cacheHitsEl = document.getElementById("stat-cache-hits")!;
  private cacheMissesEl = document.getElementById("stat-cache-misses")!;
  private musicCtxEl = document.getElementById("stat-music-ctx")!;
  private musicCountEl = document.getElementById("stat-music-count")!;
  private musicDetailEl = document.getElementById("stat-music-detail")!;
  private npcCountEl = document.getElementById("stat-npc-count")!;
  private npcDetailEl = document.getElementById("stat-npc-detail")!;

  private sessionStart = 0;
  private messageCount = 0;
  private sessionTimer = 0;
  private globalPollTimer = 0;

  setConnected(zone: string, instance: string): void {
    this.statusEl.textContent = "connected";
    this.statusEl.style.color = "#4caf50";
    this.zoneEl.textContent = `${zone}:${instance}`;
    this.sessionStart = Date.now();
    this.sessionTimer = window.setInterval(() => this.tickSession(), 1000);
    this.fetchGlobalStats();
    this.globalPollTimer = window.setInterval(() => this.fetchGlobalStats(), GLOBAL_POLL_MS);
  }

  setDisconnected(): void {
    this.statusEl.textContent = "offline";
    this.statusEl.style.color = "#e05050";
    window.clearInterval(this.sessionTimer);
    window.clearInterval(this.globalPollTimer);
  }

  setPosition(x: number, y: number): void {
    this.positionEl.textContent = `${x.toFixed(1)}, ${y.toFixed(1)}`;
  }

  setOnlineCount(count: number): void {
    this.onlineEl.textContent = String(count);
  }

  incrementMessages(): void {
    this.messageCount++;
    this.messagesEl.textContent = String(this.messageCount);
  }

  setMusicDebug(ctxState: AudioContextState, items: MusicDebugItem[]): void {
    const ctxColor = ctxState === "running" ? "#4caf50" : ctxState === "suspended" ? "#f0a030" : "#e05050";
    this.musicCtxEl.textContent = ctxState;
    this.musicCtxEl.style.color = ctxColor;
    this.musicCountEl.textContent = String(items.length);

    if (items.length === 0) {
      this.musicDetailEl.textContent = "";
      return;
    }
    this.musicDetailEl.innerHTML = items
      .map((it) => {
        const modeIcon = it.mode === "spatial" ? "🔊" : it.mode === "tuned-in" ? "🎧" : "🔇";
        const color = it.detail.includes("connected") ? "#4caf50"
          : it.detail.includes("failed") || it.detail.includes("closed") ? "#e05050"
          : "#f0a030";
        return `<div style="color:${color}">${modeIcon} ${it.userId} — ${it.detail}</div>`;
      })
      .join("");
  }

  setNpcDebug(items: SheepDebugItem[]): void {
    this.npcCountEl.textContent = String(items.length);
    if (items.length === 0) {
      this.npcDetailEl.textContent = "";
      return;
    }
    this.npcDetailEl.innerHTML = items
      .map((it) => {
        const modeIcon = it.mode === "local-wander" ? "🐑" : "🔄";
        const detail = it.idleMs !== undefined
          ? `idle ${(it.idleMs / 1000).toFixed(1)}s`
          : it.target;
        return `<div>${modeIcon} ${it.id} ${it.pos} ${detail}</div>`;
      })
      .join("");
  }

  stop(): void {
    window.clearInterval(this.sessionTimer);
    window.clearInterval(this.globalPollTimer);
  }

  private tickSession(): void {
    const elapsed = Math.floor((Date.now() - this.sessionStart) / 1000);
    const m = Math.floor(elapsed / 60);
    const s = elapsed % 60;
    this.sessionEl.textContent = `${m}:${s.toString().padStart(2, "0")}`;
  }

  private async fetchGlobalStats(): Promise<void> {
    try {
      const headers: Record<string, string> = {};
      if (activeJwt) headers["Authorization"] = `Bearer ${activeJwt}`;
      const res = await fetch(`${config.apiUrl}/api/v1/stats`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      if (typeof data.registered === "number") {
        this.registeredEl.textContent = data.registered.toLocaleString();
      }
      if (typeof data.online === "number") {
        this.globalOnlineEl.textContent = String(data.online);
      }
      if (data.cache) {
        this.cacheBackendEl.textContent = data.cache.backend ?? "—";
        this.cacheKeysEl.textContent = data.cache.keys != null ? String(data.cache.keys) : "—";
        this.cacheHitsEl.textContent = data.cache.hits != null ? String(data.cache.hits) : "n/a";
        this.cacheMissesEl.textContent = data.cache.misses != null ? String(data.cache.misses) : "n/a";
      }
    } catch {
      // silent — stats are non-critical
    }
  }
}
